<script>
  import {onMount, onDestroy, tick} from 'svelte'
  import {fade, fly} from 'svelte/transition'
  import gsap from 'gsap'
  import ScrollTrigger from 'gsap/ScrollTrigger'

  export let src = null
  export let poster
  export let animated = null
  export let animParams = null
  export let animTime = 0
  export let expandable = true

  let laptop, vp, vpFS, sc, openSC, seekTime = 0, videoShouldShow = false, videoLoaded = false, openAnim, playedExpandButton = false, videoExpanded = false

  $: if(vp != null) {
    vp.addEventListener('play', videoStarted)
  }
  $: if(src != null) {
    if(videoLoaded && vp) {
      vp.src = src
      vp.load()
    }
  }

  onMount(() => {
    if(animated) createAnim()

    sc = ScrollTrigger.create({
      trigger: laptop,
      start: 'top-=100 bottom',
      end: 'bottom+=100 top',
      onEnter: () => {
        if(!videoShouldShow) videoShouldShow = true
        if(videoLoaded && vp && vp.paused) vp.play()
      },
      onLeave: () => {
        if(videoLoaded && vp && !vp.paused) vp.pause()
      },
      onEnterBack: () => {
        if(videoLoaded && vp && vp.paused) vp.play()
      },
      onLeaveBack: () => {
        if(videoLoaded && vp && !vp.paused) vp.pause()
      }
    })
    
  })
  
  function createAnim () {
    openAnim = gsap.timeline({paused: true})
    openAnim.fromTo(laptop.querySelector('.lid'), {force3D: true, rotationX: -81, transformOrigin: '50% 90%'}, {rotationX: 0, transformOrigin: '50% 90%', ease: 'power2.out', duration: 1.5}, 0)
    openAnim.from(laptop.querySelector('.video'), {force3D: true, rotationX: -81, z: 10, transformOrigin: '50% 100% 0', ease: 'power2.out', duration: 1.5}, 0)
    openAnim.from(laptop.querySelector('.video'), {opacity: 0}, {opacity: 1, ease: 'none', duration: 1}, 0.5)
    if(animParams) {
      const toParams = {...animParams, force3D: true}
      openAnim.to(laptop.querySelector('.poster'), toParams, animTime)
    }
    if(expandable && laptop.querySelector('.expand-btn')) {
      playedExpandButton = true
      openAnim.fromTo(laptop.querySelector('.expand-btn'), {force3D: true, z: 10, scale: .5, opacity: 0}, {z: 15, scale: 1, opacity: 1, ease: 'power2.out', duration: .65}, 2)
    }
    openSC = ScrollTrigger.create({
      trigger: laptop,
      start: 'top 70%',
      toggleActions: 'play play play pause',
      animation: openAnim
    })
  }

  onDestroy(() => {
    sc?.kill()
    openSC?.kill()
    vp?.removeEventListener('play', videoStarted)
  })

  function videoStarted (e) {
    videoLoaded = true
    if(vp) {
      vp.removeEventListener('play', videoStarted)
      if(expandable && !playedExpandButton) {
        playedExpandButton = true
        gsap.fromTo(laptop.querySelector('.expand-btn'), {force3D: true, z: 10, scale: .5, opacity: 0}, {z: 15, scale: 1, opacity: 1, ease: 'power2.out', duration: .65})
      }
    }
  }

  async function expandVideo (e) {
    videoExpanded = true
    await tick()
    setTimeout(() => {
      document.addEventListener('click', clickFsBg)
      document.addEventListener('keypress', onKeyPress)
      vpFS.append(vp)
      document.body.append(vpFS)
    }, 100)
  }

  function clickFsBg (e) {
    hideExpandedVideo()
  }
  function onKeyPress (e) {
    if(e.key === 'Escape') {
      hideExpandedVideo()
    }
  }
  async function hideExpandedVideo () {
    laptop.querySelector('.video-wrap').append(vp)
    document.removeEventListener('click', clickFsBg)
    document.removeEventListener('keypress', onKeyPress)
    videoExpanded = false
  }
  
</script>
{#if videoExpanded}
<div class="video-fs" bind:this={vpFS}>
  <button class="close-btn custom" aria-label="Close">
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15.79 15.79">
      <polygon fill="currentColor" points="15.79 0.8 15 0 7.9 7.1 0.8 0 0 0.8 7.1 7.9 0 15 0.8 15.79 7.9 8.69 15 15.79 15.79 15 8.69 7.9 15.79 0.8"/>
    </svg>
  </button>
</div>
{/if}
<div class="laptop" bind:this={laptop}>
  <div class="video">
    <div class="video-wrap">
      {#if videoShouldShow && src}
      {#if expandable}
        <button class="expand-btn custom" type="button" on:click|preventDefault={expandVideo}>
          <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <symbol id="expand-arrow" viewBox="0 0 7 7">
                <path d="M1.13 1.92L6.04 6.83L6.84 6.04L1.92 1.12H5.44V0H0V5.43H1.13V1.92Z" fill="white"/>
              </symbol>
            </defs>
            <circle cx="18" cy="18" r="18" fill="black" />
            <use xlink:href="#expand-arrow" width="7" height="7" transform="translate(10.4, 10.4)" />
            <use xlink:href="#expand-arrow" width="7" height="7" transform="translate(18.75, 10.4) rotate(90, 3.5, 3.5)" />
            <use xlink:href="#expand-arrow" width="7" height="7" transform="translate(10.4, 18.75) rotate(-90, 3.5, 3.5)" />
            <use xlink:href="#expand-arrow" width="7" height="7" transform="translate(18.75, 18.75) rotate(180, 3.5, 3.5)" />
          </svg>
        </button>
      {/if}
      <video in:fade={{duration: 300}} out:fade={{duration: 300}} muted autoplay playsinline loop bind:this={vp} {poster}>
        <source {src} type="video/mp4" />
      </video>
      {/if}
      <img src={poster} class="poster" alt="Video poster frame" />
    </div>
  </div>
  <svg class="lid" width="345" height="182" viewBox="0 0 345 182" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path class="outline frame" d="M303.668 1H41.122C36.9433 1 33.5558 4.37536 33.5558 8.53908V172.707C33.5558 176.871 36.9433 180.246 41.122 180.246H303.668C307.846 180.246 311.234 176.871 311.234 172.707V8.53908C311.234 4.37536 307.846 1 303.668 1Z"/>
    <path class="outline camera" d="M172.395 9.66993C173.231 9.66993 173.908 8.99485 173.908 8.16211C173.908 7.32937 173.231 6.6543 172.395 6.6543C171.559 6.6543 170.882 7.32937 170.882 8.16211C170.882 8.99485 171.559 9.66993 172.395 9.66993Z"/>
    <path class="screen" d="M305.609 14.5698H39.6088V164.195H305.609V14.5698Z"/>
  </svg>
  <svg class="bottom" width="345" height="182" viewBox="0 0 345 182" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path class="outline base" d="M344.021 173.476V175.38C332.378 179.111 320.556 180.977 308.556 180.977C290.557 180.977 60.5692 181.023 38.1335 180.984C15.6979 180.946 0.973304 175.397 1.02136 175.38C1.05484 175.368 1.05484 174.734 1.02136 173.476H344.021Z" fill="black"/>
    <path class="outline thumb-cutout" d="M146.67 173.476H198.498V174.984C198.498 176.233 197.482 177.246 196.228 177.246H148.94C147.686 177.246 146.67 176.233 146.67 174.984V173.476Z"/>
  </svg>
</div>

<style>
  .laptop {
    position: relative;
    perspective: 800px;
    perspective-origin: 50% 50%;
  }
  .video {
    width: 76.8115942029%;
    height: 0;
    padding-bottom: 43.2%;
    position: absolute;
    left: 11.5942028986%;
    z-index: 2;
    top: 7.7%;
  }
  video, .poster {
    display: block;
    position: relative;
    width: 100%;
    height: 100%;
  }
  video {
    z-index: 2;
  }
  .poster {
    object-fit: cover;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
  }
  svg {
    width: 100%;
    height: auto;
  }
  .lid {
    position: relative;
    z-index: 0;
  }
  .bottom {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    transform: translateZ(1px);
  }
  .screen {
    fill: var(--gray-dark)
  }
  .outline {
    stroke: white
  }
  .video-wrap {
    width: 100%;
    height: 100%;
    position: absolute;
    overflow: hidden;
  }
  .expand-btn {
    position: absolute;
    bottom: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    z-index: 3;
    opacity: 0;
  }
  .expand-btn svg {
    transform: translateZ(0px);
  }
  .video-fs {
    position: fixed;
    width: 100%;
    height: 100%;
    background: rgba(0,0,0,0.8);
    z-index: 5000;
    top: 0;
    left: 0;
  }
  .video-fs :global(video) {
    position: absolute;
    width: 100%;
    height: auto;
    max-height: 720px;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    padding: 0;
  }
  .close-btn {
    position: absolute;
    right: 0;
    top: 0;
    z-index: 3;
    padding: var(--gutter-sm);
    background: black;
  }
  .close-btn svg {
    color: white;
    width: 24px;
    height: 24px;
  }
  @media(min-width: 768px) {
    .expand-btn {
      display: block;
      width: 36px;
      height: 36px;
    }
    .close-btn svg {
      width: 36px;
      height: 36px;
    }
    .close-btn {
      padding: var(--gutter-lg);
    }
    .video-fs :global(video) {
      padding: var(--gutter-lg);
    }
  }
</style>